import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule, getCurrencySymbol } from '@angular/common';
import { Store } from '@ngxs/store';
import { CookieService } from 'ngx-cookie';

import { GivebriteState, SetLocale } from '@app/state';
import { IconComponent } from '@app/ui';
import {
  CURRENCY_LOCALES,
  CurrencyLocale,
} from '@app/shared/components/donate/multi-currency/currency-locales';

/**
 * Compact currency selector for the light minimal header (checkout-family
 * routes). A quiet ghost trigger (code + chevron) opening a DaisyUI dropdown
 * menu — symbol, code, and currency name per row, active row checked. Options
 * come from `CURRENCY_LOCALES` (the same list legacy `MulticurrencyComponent`
 * renders).
 *
 * DaisyUI's focus-managed dropdown gives us keyboard access for free: the
 * trigger is a real button (Enter/Space opens), rows are real buttons
 * (Tab/Enter selects), Escape or focus loss closes. Selection blurs the row so
 * the menu dismisses without tracking open state — SSR-safe since it only runs
 * inside user-event handlers.
 *
 * Deliberately decoupled from checkout: it only reads/writes the app-level
 * currency (`GivebriteState.currency` via `SetLocale`, plus the `gb_currency`
 * cookie, mirroring legacy `MulticurrencyComponent.setLocale`). checkout-v2's
 * `CheckoutEntryService` watches that state to sync its session currency and
 * re-fetch gateways — the header knows nothing about the session services.
 */
@Component({
  selector: 'gb-header-currency-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconComponent],
  template: `
    <div class="dropdown dropdown-end" (keydown.escape)="close($event)">
      <button
        type="button"
        class="btn btn-ghost btn-sm h-8 gap-1 rounded-full px-2.5 text-xs font-semibold text-base-content/70 hover:text-base-content"
        [attr.aria-label]="'Change currency, current currency ' + currency()"
        aria-haspopup="menu"
      >
        {{ currency() }}
        <ui-icon name="chevron-down" [size]="14" class="opacity-60" />
      </button>
      <ul
        class="menu dropdown-content z-50 mt-1.5 max-h-80 w-60 flex-nowrap overflow-y-auto rounded-box border border-base-200/70 bg-base-100 p-1.5 shadow-lg"
        role="menu"
      >
        @for (locale of locales; track locale.currency) {
          <li role="none">
            <button
              type="button"
              role="menuitemradio"
              [attr.aria-checked]="locale.currency === currency()"
              class="flex items-center gap-2.5 rounded-lg px-2.5 py-2"
              [class.active]="locale.currency === currency()"
              (click)="select(locale, $event)"
            >
              <span
                class="w-8 shrink-0 text-center text-sm font-semibold tabular-nums text-base-content/70"
              >
                {{ symbolFor(locale.currency) }}
              </span>
              <span class="flex min-w-0 flex-1 flex-col text-left">
                <span class="text-sm font-medium leading-tight">{{ locale.currency }}</span>
                <span class="truncate text-xs text-base-content/50">{{ locale.label }}</span>
              </span>
              @if (locale.currency === currency()) {
                <ui-icon name="check" [size]="16" class="shrink-0" />
              }
            </button>
          </li>
        }
      </ul>
    </div>
  `,
})
export class HeaderCurrencySelectComponent {
  private readonly store = inject(Store);
  private readonly cookies = inject(CookieService);

  protected readonly locales: CurrencyLocale[] = CURRENCY_LOCALES;
  protected readonly currency = this.store.selectSignal(GivebriteState.currency);

  protected symbolFor(code: string): string {
    return getCurrencySymbol(code, 'narrow');
  }

  protected select(locale: CurrencyLocale, event: Event): void {
    // Mirror legacy MulticurrencyComponent.setLocale: state + cookie.
    this.store.dispatch(new SetLocale(locale));
    this.cookies.put('gb_currency', locale.currency);
    this.close(event);
  }

  /**
   * Blur the focused element so DaisyUI's focus-managed dropdown closes.
   * `event.target` can be a non-focusable child (icon/label span), so blur the
   * document's active element instead. Only ever runs inside user-event
   * handlers, so touching `document` here is SSR-safe.
   */
  protected close(event: Event): void {
    const active = (event.target as HTMLElement | null)?.ownerDocument
      ?.activeElement as HTMLElement | null;
    active?.blur();
  }
}
